/**
 * Error monitoring (Sentry). The SDK is loaded lazily and only when VITE_SENTRY_DSN is set, so it
 * never costs anything on the initial bundle; errors raised before it loads are queued.
 */
import { env } from "@/lib/env";

type Sentry = typeof import("@sentry/react");
let sentry: Sentry | null = null;
const queue: [unknown, Record<string, unknown> | undefined][] = [];

const SENSITIVE = /pass(word)?|pin|otp|code|secret|token|authorization|cookie|bvn|nin|account_?number|card|cvv/i;
const DIGITS = /\b\d{10,19}\b/g;

/** Redact sensitive keys and long digit runs (BVN/NIN/NUBAN/card shaped) anywhere in an object. */
export function scrub<T>(value: T, depth = 0): T {
  if (depth > 6 || value === null || value === undefined) return value;
  if (typeof value === "string") return value.replace(DIGITS, "[redacted]") as T;
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1)) as T;
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, SENSITIVE.test(k) ? "[redacted]" : scrub(v, depth + 1)]),
    ) as T;
  }
  return value;
}

export function initMonitoring(): void {
  if (!env.sentryDsn || typeof window === "undefined") return;
  void import("@sentry/react").then((S) => {
    S.init({
      dsn: env.sentryDsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      tracesSampleRate: 0.05,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.request) {
          delete event.request.cookies;
          delete event.request.data;
          if (event.request.headers) event.request.headers = scrub(event.request.headers);
          if (event.request.url) event.request.url = event.request.url.replace(DIGITS, "[redacted]");
        }
        if (event.extra) event.extra = scrub(event.extra);
        return event;
      },
      beforeBreadcrumb(crumb) {
        if (crumb.data) crumb.data = scrub(crumb.data);
        return crumb;
      },
    });
    sentry = S;
    queue.splice(0).forEach(([e, ctx]) => S.captureException(e, ctx ? { extra: ctx } : undefined));
  });
}

/** Report a caught error (UI crash, route error). Safe to call when monitoring is off. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (!env.sentryDsn) return;
  if (sentry) sentry.captureException(error, context ? { extra: scrub(context) } : undefined);
  else if (queue.length < 20) queue.push([error, context ? scrub(context) : undefined]);
}
